import client from "./client";

// ────────── Enums (wire-format strings) ──────────

export type JobPostingStatus =
  | "DRAFT"
  | "OPEN"
  | "PAUSED"
  | "CLOSED"
  | "FILLED";

export type EmploymentCommitment =
  | "FULL_TIME"
  | "PART_TIME"
  | "CONTRACT"
  | "INTERNSHIP"
  | "TEMPORARY";

export type ApplicationStage =
  | "APPLIED"
  | "SCREENING"
  | "INTERVIEW"
  | "OFFER"
  | "HIRED"
  | "REJECTED"
  | "WITHDRAWN";

export type InterviewStatus =
  | "SCHEDULED"
  | "COMPLETED"
  | "CANCELLED"
  | "NO_SHOW";

export type OfferStatus =
  | "DRAFT"
  | "EXTENDED"
  | "ACCEPTED"
  | "DECLINED"
  | "WITHDRAWN"
  | "EXPIRED";

export type CandidateSource =
  | "REFERRAL"
  | "JOB_BOARD"
  | "COMPANY_SITE"
  | "AGENCY"
  | "LINKEDIN"
  | "OUTBOUND"
  | "OTHER";

// ────────── Entity Types ──────────

// Mirror of backend ``JobPostingRead`` in backend/app/schemas/recruiting.py.
// Keep field names aligned with the backend exactly — mismatches caused
// a render crash ("Something went wrong on this page") because the list
// view dereferenced `job.employment_commitment`, which doesn't exist.
export interface JobPosting {
  id: string;
  org_id: string;
  department_id: string | null;
  hiring_manager_id: string | null;
  title: string;
  slug: string;
  description: string | null;
  status: JobPostingStatus;
  commitment: EmploymentCommitment;
  location: string | null;
  is_remote: boolean;
  salary_min: number | null;
  salary_max: number | null;
  salary_currency: string | null;
  num_positions: number;
  open_date: string | null;
  close_date: string | null;
  /** Public-careers-page surface state. Both default to safe nulls
      so older rows hydrate cleanly. */
  is_public_careers_listed?: boolean;
  published_careers_at?: string | null;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface JobPostingCreate {
  title: string;
  slug?: string | null;
  description?: string | null;
  department_id?: string | null;
  location?: string | null;
  commitment?: EmploymentCommitment;
  status?: JobPostingStatus;
  is_remote?: boolean;
  salary_min?: number | null;
  salary_max?: number | null;
  salary_currency?: string | null;
  num_positions?: number;
  open_date?: string | null;
  close_date?: string | null;
  hiring_manager_id?: string | null;
}

export interface Candidate {
  id: string;
  org_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  linkedin_url: string | null;
  resume_url: string | null;
  source: CandidateSource;
  notes: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CandidateCreate {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string | null;
  linkedin_url?: string | null;
  resume_url?: string | null;
  source?: CandidateSource;
  notes?: string | null;
}

export interface AIScoreBreakdown {
  overall_score: number;
  skills_match: number;
  experience_match: number;
  culture_signals: string[];
  red_flags: string[];
  summary: string;
  /** Set when scoring failed; carries the underlying reason. */
  error?: string;
}

export interface Application {
  id: string;
  org_id: string;
  job_posting_id: string;
  candidate_id: string;
  stage: ApplicationStage;
  rejection_reason: string | null;
  applied_at: string;
  stage_changed_at: string;
  created_at: string;
  updated_at: string;
  candidate?: Candidate | null;
  job_posting?: JobPosting | null;
  /** Lenavio Score — 0..100. NULL until first scoring run. */
  ai_score?: number | null;
  ai_score_breakdown?: AIScoreBreakdown | null;
  ai_scored_at?: string | null;
  ai_score_model?: string | null;
}

export interface BulkScoreResult {
  total: number;
  scored: number;
  skipped: number;
  failed: number;
}

export async function scoreApplication(
  applicationId: string,
): Promise<Application> {
  const res = await client.post<Application>(
    `/recruiting/applications/${applicationId}/score`,
  );
  return res.data;
}

export async function scoreAllApplicantsForJob(
  jobId: string,
  opts?: { only_unscored?: boolean },
): Promise<BulkScoreResult> {
  const res = await client.post<BulkScoreResult>(
    `/recruiting/jobs/${jobId}/score-all`,
    null,
    { params: opts },
  );
  return res.data;
}


// ─── Lenavio Draft (AI JD generator) ────────────────────────────────

export type JDTone = "professional" | "casual" | "startup";

export interface GenerateJDPayload {
  title: string;
  department?: string;
  level?: string;
  employment_type?: string;
  location?: string;
  key_requirements?: string[];
  salary_range?: string;
  tone?: JDTone;
}

export interface GenerateJDResponse {
  description: string; // markdown
  model: string;
}

export async function generateJobDescription(
  payload: GenerateJDPayload,
): Promise<GenerateJDResponse> {
  const res = await client.post<GenerateJDResponse>(
    "/recruiting/generate-jd",
    payload,
  );
  return res.data;
}

export async function publishJob(
  jobId: string,
  isPublic: boolean,
): Promise<JobPosting> {
  const res = await client.post<JobPosting>(
    `/recruiting/jobs/${jobId}/publish`,
    { is_public_careers_listed: isPublic },
  );
  return res.data;
}


// ─── Public careers (no auth) ────────────────────────────────────────

export interface PublicJobSummary {
  id: string;
  title: string;
  location: string | null;
  is_remote: boolean;
  commitment: EmploymentCommitment;
  department_name: string | null;
  salary_min: number | null;
  salary_max: number | null;
  salary_currency: string | null;
  open_date: string | null;
}

export interface PublicJobDetail extends PublicJobSummary {
  description: string | null;
  org_name: string;
  org_logo_url: string | null;
  apply_url: string;
}

export interface PublicCareersResponse {
  org_name: string;
  org_logo_url: string | null;
  org_slug: string;
  jobs: PublicJobSummary[];
}

export interface PublicApplyPayload {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  linkedin_url?: string;
  resume_url?: string;
  cover_letter?: string;
}

/** Public careers list — no auth header sent. */
export async function getPublicCareers(
  orgSlug: string,
): Promise<PublicCareersResponse> {
  const res = await client.get<PublicCareersResponse>(
    `/public/careers/${orgSlug}`,
  );
  return res.data;
}

export async function getPublicJob(
  orgSlug: string,
  jobId: string,
): Promise<PublicJobDetail> {
  const res = await client.get<PublicJobDetail>(
    `/public/careers/${orgSlug}/${jobId}`,
  );
  return res.data;
}

export async function applyToPublicJob(
  orgSlug: string,
  jobId: string,
  payload: PublicApplyPayload,
): Promise<{ status: string; message: string }> {
  const res = await client.post(
    `/public/careers/${orgSlug}/${jobId}/apply`,
    payload,
  );
  return res.data;
}

export interface ApplicationCreate {
  job_posting_id: string;
  candidate_id: string;
  stage?: ApplicationStage;
}

export interface Interview {
  id: string;
  org_id: string;
  application_id: string;
  interviewer_id: string;
  scheduled_at: string;
  duration_minutes: number;
  location: string | null;
  status: InterviewStatus;
  rating: number | null;
  recommendation: string | null;
  feedback: string | null;
  created_at: string;
  updated_at: string;
}

export interface InterviewCreate {
  application_id: string;
  interviewer_id: string;
  scheduled_at: string;
  duration_minutes?: number;
  location?: string | null;
}

export interface InterviewFeedback {
  rating: number;
  recommendation: string;
  feedback: string;
}

export interface Offer {
  id: string;
  org_id: string;
  application_id: string;
  salary_amount: number;
  currency: string;
  start_date: string;
  expires_at: string | null;
  status: OfferStatus;
  notes: string | null;
  extended_at: string | null;
  responded_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface OfferCreate {
  application_id: string;
  salary_amount: number;
  currency?: string;
  start_date: string;
  expires_at?: string | null;
  notes?: string | null;
}

// ────────── Job Postings ──────────

export async function listJobPostings(params?: {
  status?: JobPostingStatus;
  include_archived?: boolean;
}): Promise<JobPosting[]> {
  const { data } = await client.get<JobPosting[]>("/recruiting/jobs", {
    params,
  });
  return data;
}

export async function createJobPosting(
  payload: JobPostingCreate,
): Promise<JobPosting> {
  const { data } = await client.post<JobPosting>("/recruiting/jobs", payload);
  return data;
}

export async function getJobPosting(id: string): Promise<JobPosting> {
  const { data } = await client.get<JobPosting>(`/recruiting/jobs/${id}`);
  return data;
}

export async function updateJobPosting(
  id: string,
  payload: Partial<JobPostingCreate>,
): Promise<JobPosting> {
  const { data } = await client.patch<JobPosting>(
    `/recruiting/jobs/${id}`,
    payload,
  );
  return data;
}

export async function archiveJobPosting(id: string): Promise<void> {
  await client.delete(`/recruiting/jobs/${id}`);
}

// ────────── Candidates ──────────

export async function listCandidates(params?: {
  search?: string;
  include_archived?: boolean;
}): Promise<Candidate[]> {
  const { data } = await client.get<Candidate[]>("/recruiting/candidates", {
    params,
  });
  return data;
}

export async function createCandidate(
  payload: CandidateCreate,
): Promise<Candidate> {
  const { data } = await client.post<Candidate>(
    "/recruiting/candidates",
    payload,
  );
  return data;
}

export async function getCandidate(id: string): Promise<Candidate> {
  const { data } = await client.get<Candidate>(
    `/recruiting/candidates/${id}`,
  );
  return data;
}

export async function updateCandidate(
  id: string,
  payload: Partial<CandidateCreate>,
): Promise<Candidate> {
  const { data } = await client.patch<Candidate>(
    `/recruiting/candidates/${id}`,
    payload,
  );
  return data;
}

export async function archiveCandidate(id: string): Promise<void> {
  await client.delete(`/recruiting/candidates/${id}`);
}

// ────────── Applications ──────────

export async function listApplications(params?: {
  job_id?: string;
  candidate_id?: string;
  stage?: ApplicationStage;
}): Promise<Application[]> {
  const { data } = await client.get<Application[]>(
    "/recruiting/applications",
    { params },
  );
  return data;
}

export async function createApplication(
  payload: ApplicationCreate,
): Promise<Application> {
  const { data } = await client.post<Application>(
    "/recruiting/applications",
    payload,
  );
  return data;
}

export async function getApplication(id: string): Promise<Application> {
  const { data } = await client.get<Application>(
    `/recruiting/applications/${id}`,
  );
  return data;
}

export async function changeApplicationStage(
  id: string,
  payload: { new_stage: ApplicationStage; rejection_reason?: string },
): Promise<Application> {
  const { data } = await client.patch<Application>(
    `/recruiting/applications/${id}/stage`,
    payload,
  );
  return data;
}

export async function deleteApplication(id: string): Promise<void> {
  await client.delete(`/recruiting/applications/${id}`);
}

// ────────── Interviews ──────────

export async function listInterviews(params?: {
  application_id?: string;
  interviewer_id?: string;
}): Promise<Interview[]> {
  const { data } = await client.get<Interview[]>("/recruiting/interviews", {
    params,
  });
  return data;
}

export async function createInterview(
  payload: InterviewCreate,
): Promise<Interview> {
  const { data } = await client.post<Interview>(
    "/recruiting/interviews",
    payload,
  );
  return data;
}

export async function getInterview(id: string): Promise<Interview> {
  const { data } = await client.get<Interview>(
    `/recruiting/interviews/${id}`,
  );
  return data;
}

export async function submitInterviewFeedback(
  id: string,
  payload: InterviewFeedback,
): Promise<Interview> {
  const { data } = await client.patch<Interview>(
    `/recruiting/interviews/${id}/feedback`,
    payload,
  );
  return data;
}

export async function cancelInterview(id: string): Promise<Interview> {
  const { data } = await client.post<Interview>(
    `/recruiting/interviews/${id}/cancel`,
  );
  return data;
}

// ────────── Offers ──────────

export async function listOffers(params?: {
  application_id?: string;
  status?: OfferStatus;
}): Promise<Offer[]> {
  const { data } = await client.get<Offer[]>("/recruiting/offers", {
    params,
  });
  return data;
}

export async function createOffer(payload: OfferCreate): Promise<Offer> {
  const { data } = await client.post<Offer>("/recruiting/offers", payload);
  return data;
}

export async function getOffer(id: string): Promise<Offer> {
  const { data } = await client.get<Offer>(`/recruiting/offers/${id}`);
  return data;
}

export async function extendOffer(id: string): Promise<Offer> {
  const { data } = await client.post<Offer>(
    `/recruiting/offers/${id}/extend`,
  );
  return data;
}

export async function respondToOffer(
  id: string,
  accepted: boolean,
): Promise<Offer> {
  const { data } = await client.post<Offer>(
    `/recruiting/offers/${id}/respond`,
    { accepted },
  );
  return data;
}

export async function withdrawOffer(id: string): Promise<Offer> {
  const { data } = await client.post<Offer>(
    `/recruiting/offers/${id}/withdraw`,
  );
  return data;
}


// ─────────── Talent rediscovery — internal candidate matches ───────────

export interface InternalMatch {
  id: string;
  job_id: string;
  employee_id: string;
  employee_name: string | null;
  current_role: string | null;
  department: string | null;
  match_score: number;
  matching_skills: string[];
  gaps: string[];
  rationale: string | null;
  development_time: string | null;
  created_at: string;
}

export interface InternalMatchesResponse {
  job_id: string;
  matches: InternalMatch[];
  last_scanned_at: string | null;
}

export async function rediscoverInternalCandidates(
  jobId: string,
): Promise<InternalMatchesResponse> {
  const { data } = await client.post<InternalMatchesResponse>(
    `/recruiting/jobs/${jobId}/rediscover`,
  );
  return data;
}

export async function listInternalMatches(
  jobId: string,
): Promise<InternalMatchesResponse> {
  const { data } = await client.get<InternalMatchesResponse>(
    `/recruiting/jobs/${jobId}/internal-matches`,
  );
  return data;
}
